import { css, cx } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import type { IconProps, IconSize } from "@octopusdeploy/design-system-icons";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import type React from "react";
export type BaseStatuses = "success" | "executing" | "warning" | "failure" | "neutral";
export type BaseStatusSize = 28 | 32 | 36 | 40;
export interface BaseStatusProps {
    icon: React.ComponentType<IconProps>;
    status: BaseStatuses;
    iconVariant: "default" | "inverse";
    label: string;
    size: BaseStatusSize;
    spinning?: boolean;
    tooltip?: string;
}
export function BaseStatus({ icon: StatusIcon, status, iconVariant, label, size, spinning, tooltip }: BaseStatusProps) {
    const iconColor = iconVariant === "default" ? themeTokens.color.healthStatus.icon.default : themeTokens.color.healthStatus.icon.inverse;
    const icon = <StatusIcon size={iconSizeMap[size]} color={iconColor} title={label}/>;
    const container = <div className={cx(styles.root, styles.sizes[size], styles.status[status])}>{spinning ? <span className={styles.spinning}>{icon}</span> : icon}</div>;
    return tooltip ? <Tooltip content={tooltip}>{container}</Tooltip> : container;
}
const iconSizeMap: Record<BaseStatusSize, IconSize> = {
    28: 16,
    32: 20,
    36: 24,
    40: "font-size", // this translates to 28, due to setting the font-size on the root styles below
};
const styles = {
    root: css({
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "28px", // Using px units because this should not get bigger if the user sets a custom font size in their browser, since the other units in this component are all px.
        borderRadius: borderRadius.circle,
    }),
    status: {
        success: css({ backgroundColor: themeTokens.color.healthStatus.background.success }),
        executing: css({ backgroundColor: themeTokens.color.healthStatus.background.executing }),
        warning: css({ backgroundColor: themeTokens.color.healthStatus.background.warning }),
        failure: css({ backgroundColor: themeTokens.color.healthStatus.background.failure }),
        neutral: css({ backgroundColor: themeTokens.color.healthStatus.background.neutral }),
    },
    sizes: {
        28: css({ width: 28, height: 28 }),
        32: css({ width: 32, height: 32 }),
        36: css({ width: 36, height: 36 }),
        40: css({ width: 40, height: 40 }),
    },
    spinning: css({
        animation: "spin 1s steps(8) infinite",
        "@keyframes spin": {
            from: { transform: "rotate(0deg)" },
            to: { transform: "rotate(360deg)" },
        },
    }),
};
